import * as React from "react";
import { Card } from "../../card";
import { StatCard } from "../stat-card";
import { DataTable } from "../data-table";
import { SectionTitle } from "../section-title";
import { formatBytes } from "../../format";
import { useApi } from "../../api/client";
import { retention as retentionContract } from "../../api/contracts/database/retention";
import type { RetentionHealthDto } from "../../api/contracts/database/retention";

/**
 * /database Health card (Data Design PRD §7.3). Read-only: DB size, events/day,
 * top tables by row count (a COUNT-based estimate — the physical `dbstat` vtable
 * isn't in this SQLite build), and the prune preview under the current
 * settings-scoped policy (with a "no policy set" state). The three size/growth
 * tiles are a uniform StatCard row — no lone sparkline among plain tiles (Issue
 * 26 #2). All data flows through the zod contract + createRoute (no raw fetch).
 */
export function RetentionHealthCard() {
  const { data, isLoading } = useApi(retentionContract.health, {});

  if (isLoading && !data) {
    return (
      <Card className="p-6">
        <SectionTitle variant="plain" title="Health" />
        <p className="mt-3 type-body text-muted-foreground">Loading database health…</p>
      </Card>
    );
  }
  if (!data) return null;

  const h: RetentionHealthDto = data;
  const growthValues = h.growth.map((g) => g.events);
  const growthTotal = growthValues.reduce((s, n) => s + n, 0);
  const preview = h.preview;

  return (
    <Card className="space-y-5 p-6">
      <SectionTitle
        variant="plain"
        title="Health"
        subtitle="Local database size, growth, and what retention would reclaim."
      />

      {/* Size + growth stat row — a uniform row of StatCards (no lone graph:
          metric rows are all-sparkline or none, Issue 26 #2). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          title="Database size"
          value={formatBytes(h.logicalBytes)}
          description={`${formatBytes(h.fileBytes)} on disk`}
        />
        <StatCard
          title="Events / day"
          value={growthTotal.toLocaleString()}
          description={`Last ${h.growth.length} days`}
        />
        <StatCard
          title="Reclaimable now"
          value={preview.hasPolicy ? formatBytes(preview.totalBytes) : "—"}
          description={
            preview.hasPolicy
              ? `${preview.eventRows.toLocaleString()} payload(s) · ${preview.rawJsonRows.toLocaleString()} raw version(s)`
              : "No retention policy set"
          }
        />
      </div>

      {/* Top tables by row count. */}
      <div className="space-y-2">
        <div className="type-body font-medium text-foreground">Top tables</div>
        {/* Break the table out of the card's px-6 gutter (-mx-6) so its header
            band + rows stretch edge-to-edge — the table's own first:pl-6/last:pr-6
            becomes the single 24px gutter, matching the Objects inventory table
            (never double-inset inside a padded card). */}
        <div className="-mx-6">
          <DataTable
            wrapInCard={false}
            columns={[
              { key: "name", header: "Table", render: (r) => r.name },
              {
                key: "rows",
                header: "Rows",
                render: (r) => (r.rows < 0 ? "—" : r.rows.toLocaleString()),
              },
            ]}
            rows={h.topTables}
            rowKey={(r) => r.name}
          />
        </div>
      </div>

      {/* Prune preview — per-category, or the empty/no-policy state. */}
      <div className="space-y-2">
        <div className="type-body font-medium text-foreground">Retention preview</div>
        {!preview.hasPolicy ? (
          <p className="type-body text-muted-foreground">
            No retention policy set — nothing is pruned (fail-closed). Set a
            horizon in the retention controls above to reclaim space from old
            event payloads and superseded integration records.
          </p>
        ) : preview.categories.length === 0 ? (
          <p className="type-body text-muted-foreground">
            Nothing to reclaim right now — no rows are past the horizon.
          </p>
        ) : (
          <div className="-mx-6">
            <DataTable
              wrapInCard={false}
              columns={[
                { key: "category", header: "Category", render: (c) => c.category },
                {
                  key: "rows",
                  header: "Rows",
                  render: (c) => c.rows.toLocaleString(),
                },
                {
                  key: "bytes",
                  header: "Reclaimable",
                  render: (c) => formatBytes(c.bytes),
                },
              ]}
              rows={preview.categories}
              rowKey={(c) => c.category}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
